'use client'

/**
 * UserMenu - avatar + dropdown de conta.
 * Identidade Visual eBuz v1.0 (Manual seção 13 - topbar pattern).
 *
 * - Avatar: w-8 h-8 rounded-full bg-brand-grad com iniciais (font-bold).
 * - Dropdown: bg-paper, border ink-7, shadow-soft.
 * - Items: hover bg-ink-9, text ink-1, ativo text-ink-0 + bg-ink-9 + font-semibold.
 *
 * API mantida.
 */

import { useState, useRef, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'

interface UserMenuProps {
  userName?: string
  userEmail?: string
  avatarUrl?: string | null
  isAdmin: boolean
  isWhitelabel?: boolean
  credits?: number
}

interface MenuItem {
  label: string
  href: string
}

interface MenuSection {
  title: string
  items: MenuItem[]
}

const PERSONAL_ITEMS: MenuItem[] = [
  { label: 'Meu perfil', href: '/app/perfil' },
  { label: 'Minhas compras', href: '/app/minhas-compras' },
  { label: 'Meus créditos', href: '/app/creditos' },
]

const ADMIN_SECTIONS: MenuSection[] = [
  {
    title: 'Gestão',
    items: [
      { label: 'Dashboard', href: '/admin' },
      { label: 'Usuários', href: '/admin/usuarios' },
      { label: 'CRM', href: '/admin/chamados' },
    ],
  },
  {
    title: 'Conteúdo',
    items: [
      { label: 'Fluxos', href: '/admin/fluxos' },
      { label: 'Planos', href: '/admin/planos' },
      { label: 'Provedores IA', href: '/admin/ia' },
      { label: 'Páginas', href: '/admin/paginas' },
    ],
  },
  {
    title: 'Financeiro',
    items: [
      { label: 'Moedas', href: '/admin/creditos' },
      { label: 'Cupons', href: '/admin/cupons' },
    ],
  },
  {
    title: 'Sistema',
    items: [
      { label: 'Notificações', href: '/admin/notificacoes' },
      { label: 'Configurações', href: '/admin/configuracoes' },
    ],
  },
]

export function UserMenu({ userName, userEmail, avatarUrl, isAdmin, isWhitelabel = false, credits }: UserMenuProps) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname()
  const router = useRouter()

  const iniciais = userName
    ? userName.split(' ').slice(0, 2).map((p: string) => p[0]).join('').toUpperCase()
    : (userEmail?.[0] ?? '?').toUpperCase()

  // Fecha ao clicar fora
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  // Fecha ao navegar
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  // Fecha com ESC
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  function handleLogout() {
    // Top-level navigation pro endpoint GET. O endpoint:
    // 1. Destroi a sessao local (limpa cookie)
    // 2. Redireciona pro Zitadel end_session (SLO)
    // 3. Zitadel desloga e redireciona pra /login?reset=logout
    //
    // Usar fetch POST aqui quebrava: o redirect 307 era seguido pelo
    // fetch ate o Zitadel (cross-origin), CORS bloqueava, exception
    // engolia o router.push e o cliente ficava preso na mesma tela.
    window.location.href = '/api/auth/logout'
  }

  function isActive(href: string) {
    if (href === '/admin' || href === '/app') return pathname === href
    return pathname?.startsWith(href) ?? false
  }

  return (
    <div ref={menuRef} className="relative">
      {/* Avatar button */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
        aria-label="Menu do usuário"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {avatarUrl ? (
          <Image
            src={avatarUrl}
            alt="Avatar"
            width={32}
            height={32}
            className="w-8 h-8 rounded-full object-cover ring-2 ring-ink-7"
            unoptimized
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-brand-grad text-white grid place-items-center text-[12px] font-bold tracking-tight">
            {iniciais}
          </div>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <>
          {/* Backdrop mobile */}
          <div
            className="fixed inset-0 z-40 sm:bg-transparent bg-ink-0/20"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          <div
            role="menu"
            className="absolute right-0 top-full mt-2 w-60 bg-paper border border-ink-7 rounded-md shadow-soft z-50 animate-scale-in max-h-[70vh] overflow-y-auto overscroll-contain scrollbar-thin"
          >
            {/* User info */}
            {userName && (
              <div className="px-4 py-3 border-b border-ink-7">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-brand-grad text-white grid place-items-center text-[12px] font-bold shrink-0">
                    {iniciais}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink-1 truncate">{userName}</p>
                    {userEmail && (
                      <p className="text-[11px] text-ink-4 truncate">{userEmail}</p>
                    )}
                  </div>
                </div>
                {credits !== undefined && (
                  <div className="mt-2 flex items-center justify-between bg-ink-9 border border-ink-7 rounded-md px-2.5 py-1.5">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-4">moedas</span>
                    <span className="text-[12px] font-semibold text-ink-1 tabular-nums">{credits}</span>
                  </div>
                )}
              </div>
            )}

            {/* Personal items */}
            <div className="py-1">
              {PERSONAL_ITEMS.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  role="menuitem"
                  className={`flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                    isActive(item.href)
                      ? 'text-ink-0 bg-ink-9 font-medium'
                      : 'text-ink-3 hover:bg-ink-9 hover:text-ink-1'
                  }`}
                >
                  {item.label === 'Meu perfil' ? (
                    <svg className="w-4 h-4 text-ink-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M5 21a7 7 0 0114 0" />
                    </svg>
                  ) : item.label === 'Minhas compras' ? (
                    <svg className="w-4 h-4 text-ink-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 7h14l-1.5 11a2 2 0 01-2 1.7H8.5a2 2 0 01-2-1.7L5 7z" />
                      <path d="M9 7V5a3 3 0 016 0v2" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-ink-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v10M9 9.5c0-.83.67-1.5 1.5-1.5h3a1.5 1.5 0 010 3h-3a1.5 1.5 0 00 0 3h3a1.5 1.5 0 010 0" />
                    </svg>
                  )}
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Admin sections */}
            {isAdmin && !isWhitelabel && ADMIN_SECTIONS.map((section) => (
              <div key={section.title}>
                <div className="px-4 pt-2 pb-1 border-t border-ink-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-4">
                    {section.title}
                  </p>
                </div>
                <div className="pb-1">
                  {section.items.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      role="menuitem"
                      className={`flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                        isActive(item.href)
                          ? 'text-ink-0 bg-ink-9 font-medium'
                          : 'text-ink-3 hover:bg-ink-9 hover:text-ink-1'
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            {/* Logout */}
            <div className="border-t border-ink-7 py-1">
              <button
                onClick={handleLogout}
                role="menuitem"
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-danger hover:bg-danger-bg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sair
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
